<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TwoFactorMessagesAPI from 'dashboard/api/twoFactorMessages';
import { messageTimestamp } from 'shared/helpers/timeHelper';

const { t } = useI18n();

const records = ref([]);
const isLoading = ref(false);

const fetchTwoFactorMessages = async () => {
  isLoading.value = true;

  try {
    const response = await TwoFactorMessagesAPI.get();
    records.value = response.data;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('TWO_FACTOR_MESSAGES.API.ERROR_MESSAGE')
    );
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchTwoFactorMessages();
});

const tableHeaders = computed(() => {
  return [
    t('TWO_FACTOR_MESSAGES.LIST.TABLE_HEADER.AGENT'),
    t('TWO_FACTOR_MESSAGES.LIST.TABLE_HEADER.STATUS'),
    t('TWO_FACTOR_MESSAGES.LIST.TABLE_HEADER.REQUEST'),
    t('TWO_FACTOR_MESSAGES.LIST.TABLE_HEADER.DELIVERY'),
    t('TWO_FACTOR_MESSAGES.LIST.TABLE_HEADER.CONTEXT'),
  ];
});

const formatTimestamp = timestamp => {
  return timestamp ? messageTimestamp(timestamp, 'MMM dd, yyyy hh:mm a') : '-';
};

const formatLocation = attempt => {
  const location = attempt.location_data || {};
  const parts = [location.city, location.region, location.country].filter(
    Boolean
  );

  return parts.length ? parts.join(', ') : location.country_code || '-';
};

const userName = attempt => {
  return attempt.user?.name || t('APP_GLOBAL.DELETED_USER');
};

const userEmail = attempt => {
  return attempt.user?.email || '';
};

const statusLabel = status => {
  return t(`TWO_FACTOR_MESSAGES.STATUS.${status.toUpperCase()}`);
};

const statusClass = status => {
  const classes = {
    issued:
      'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    verified:
      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
    failed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
    expired:
      'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    delivery_error:
      'bg-ruby-100 text-ruby-700 dark:bg-ruby-900/40 dark:text-ruby-200',
  };

  return (
    classes[status] ||
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
  );
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('TWO_FACTOR_MESSAGES.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('TWO_FACTOR_MESSAGES.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('TWO_FACTOR_MESSAGES.HEADER')"
        :description="$t('TWO_FACTOR_MESSAGES.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="min-w-full overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-75 dark:divide-slate-700">
          <thead>
            <th
              v-for="header in tableHeaders"
              :key="header"
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ header }}
            </th>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <tr v-for="attempt in records" :key="attempt.id">
              <td class="py-4 ltr:pr-4 rtl:pl-4 align-top min-w-56">
                <div class="font-medium">
                  {{ userName(attempt) }}
                </div>
                <div v-if="userEmail(attempt)" class="text-sm text-n-slate-10">
                  {{ userEmail(attempt) }}
                </div>
              </td>

              <td class="py-4 ltr:pr-4 rtl:pl-4 align-top min-w-44">
                <span
                  class="inline-flex px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wide"
                  :class="statusClass(attempt.status)"
                >
                  {{ statusLabel(attempt.status) }}
                </span>
                <div
                  v-if="attempt.failure_reason"
                  class="mt-2 text-sm text-n-slate-10 break-words"
                >
                  {{ attempt.failure_reason }}
                </div>
              </td>

              <td class="py-4 ltr:pr-4 rtl:pl-4 align-top min-w-52">
                <div class="font-medium">
                  {{ formatTimestamp(attempt.requested_at) }}
                </div>
                <div class="mt-2 text-sm text-n-slate-10">
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.EXPIRES') }}:
                  {{ formatTimestamp(attempt.expires_at) }}
                </div>
                <div
                  v-if="attempt.verified_at"
                  class="mt-1 text-sm text-n-slate-10"
                >
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.VERIFIED') }}:
                  {{ formatTimestamp(attempt.verified_at) }}
                </div>
              </td>

              <td class="py-4 ltr:pr-4 rtl:pl-4 align-top min-w-56">
                <div class="text-sm">
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.SENT') }}:
                  {{ formatTimestamp(attempt.sent_at) }}
                </div>
                <div class="mt-2 text-sm text-n-slate-10">
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.VERIFICATION_ATTEMPTS') }}:
                  {{ attempt.verification_attempts_count }}
                </div>
                <div
                  v-if="attempt.additional_attributes?.telegram_message_id"
                  class="mt-1 text-sm text-n-slate-10"
                >
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.MESSAGE_ID') }}:
                  {{ attempt.additional_attributes.telegram_message_id }}
                </div>
              </td>

              <td class="py-4 align-top min-w-72">
                <div class="text-sm">
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.IP_ADDRESS') }}:
                  {{ attempt.request_ip || '-' }}
                </div>
                <div class="mt-2 text-sm text-n-slate-10">
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.LOCATION') }}:
                  {{ formatLocation(attempt) }}
                </div>
                <div class="mt-2 text-sm text-n-slate-10 break-words">
                  {{ $t('TWO_FACTOR_MESSAGES.LIST.DETAILS.USER_AGENT') }}:
                  {{ attempt.user_agent || '-' }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </SettingsLayout>
</template>
